import { STChecklistItem, STDocument, STMissingReason } from '@st/sdk'
import {
  Button,
  CircleXIcon,
  Dialog,
  DialogButtons,
  MiniDocumentDropZone,
  Modal,
  PaperClipIcon,
  Select,
  Spinner
} from '@st/theme'
import { Progress } from '@st/util/progress'
import { clsx } from 'clsx'
import { match, P } from '@st/pattern'
import { Upload } from '~/features/document-uploads'
import { useDocumentUploads } from '~/features/document-uploads/use-document-uploads'
import { useProcess, useSelect } from '~/store'
import { ConfirmDeleteDocumentDialog } from '../formation-viewer/formation-wizard/confirm-delete-document-dialog'
import {
  selClientUploadDocuments,
  type ClientUploadDocumentsModel
} from './client-organizer-selectors'
import { ClientCard, ClientCardStepHeading, DocumentChecklistItem } from './ui'

import { DocumentType } from '@st/ui-config'

type Props = {
  documentType?: DocumentType
  stepNumber?: number
  variant?: 'card' | 'dialog'
  isLocked?: boolean
  onClose?: () => void
}
export function ClientUploadDocumentsCard({
  documentType,
  stepNumber,
  variant = 'card',
  isLocked = false,
  onClose
}: Props) {
  const documentUploads = useDocumentUploads()
  const stFolder = useProcess('stFolder')
  const route = useProcess('route')

  const model = useSelect(selClientUploadDocuments)
  return match(model)
    .with({ status: P.union('idle', 'loading', 'unauthorized') }, () => null)
    .with({ status: 'ready' }, (model) => {
      const { folder, dialog } = model

      const documentsView = (
        <ClientUploadDocumentsView
          model={model}
          isLocked={isLocked}
          onUploadFileToChecklistItem={(file, checklistItemId) =>
            documentUploads.upload({
              file,
              checklistItemId,
              folderId: folder.id,
              documentTypeId: documentType?.id
            })
          }
          onClickDocument={(doc) => route.send({ type: 'openUrl', url: doc.urls.view! })}
          onClickDeleteDocument={(doc) => stFolder.send({ type: 'openUploadDeleteDialog', doc })}
          onClickSetIncompleteReason={(checklistItem) =>
            stFolder.send({ type: 'openUploadReasonDialog', checklistItem })
          }
          onUploadFilesToOther={(files) => {
            for (const file of files) {
              documentUploads.upload({
                file,
                folderId: folder.id,
                documentTypeId: documentType?.id,
                questionnaireDocumentTypeId: documentType?.id
              })
            }
          }}
        />
      )
      const closeDialog = () => stFolder.send({ type: 'closeUploadDialog' })

      return (
        <>
          {match(variant)
            .with('dialog', () => (
              <Dialog
                title={`Your ${folder.year} documents`}
                subtitle={model.instructions}
                className="max-w-4xl"
                buttons={
                  <DialogButtons>
                    <Button variant="primary" onClick={onClose}>
                      OK
                    </Button>
                  </DialogButtons>
                }
              >
                {documentsView}
              </Dialog>
            ))
            .with('card', () => (
              <ClientCard>
                <div className="space-y-5">
                  <ClientCardStepHeading
                    stepNumber={stepNumber!}
                    completed={model.checklistCompleted}
                    title={
                      <h2 className="text-xl font-semibold text-gray-900">Upload documents</h2>
                    }
                    status={<span className="text-base leading-6">{model.instructions}</span>}
                  />
                  {documentsView}
                </div>
              </ClientCard>
            ))
            .exhaustive()}
          {match(dialog)
            .with({ type: 'confirmDelete' }, ({ doc }) => (
              <Modal isOpen={true}>
                <ConfirmDeleteDocumentDialog
                  doc={doc}
                  onConfirm={() => stFolder.send({ type: 'confirmUploadDelete' })}
                  onCancel={closeDialog}
                />
              </Modal>
            ))
            .with({ type: 'setIncompleteReason' }, (dialog) => (
              <Modal isOpen={true}>
                <SetChecklistItemIncompleteReasonDialog
                  reason={dialog.reason}
                  missingReasonOptions={dialog.options}
                  cancelLabel={dialog.cancelLabel}
                  onChange={(reason) => stFolder.send({ type: 'uploadReasonChanged', reason })}
                  onSave={() => stFolder.send({ type: 'saveUploadReason' })}
                  onCancel={() => stFolder.send({ type: 'removeUploadReason' })}
                />
              </Modal>
            ))
            .otherwise(() => null)}
        </>
      )
    })
    .exhaustive()
}

type ClientUploadDocumentsViewProps = {
  model: ClientUploadDocumentsModel
  onUploadFileToChecklistItem: (file: File, checklistItemId: string) => void
  onClickDocument: (doc: STDocument) => void
  onClickDeleteDocument: (doc: STDocument) => void
  onClickSetIncompleteReason: (checklistItem: STChecklistItem) => void
  onUploadFilesToOther: (files: File[]) => void
  isLocked: boolean
}
function ClientUploadDocumentsView({
  model,
  onUploadFileToChecklistItem,
  onClickDocument,
  onClickDeleteDocument,
  onClickSetIncompleteReason,
  onUploadFilesToOther,
  isLocked
}: ClientUploadDocumentsViewProps) {
  return (
    <div className="flex flex-col gap-4">
      <UploadDocumentsChecklist
        rows={model.checklistRows}
        isLocked={isLocked}
        onClickDocument={onClickDocument}
        onClickDeleteDocument={onClickDeleteDocument}
        onClickSetIncompleteReason={onClickSetIncompleteReason}
        onUpload={onUploadFileToChecklistItem}
      />
      <UploadOtherDocuments
        title={model.otherDocumentsTitle}
        uploads={model.otherUploads}
        otherDocuments={model.otherDocuments}
        isLocked={isLocked}
        onClickDocument={onClickDocument}
        onClickDeleteDocument={onClickDeleteDocument}
        onUpload={onUploadFilesToOther}
      />
    </div>
  )
}

type UploadDocumentsChecklistProps = {
  rows: ClientUploadDocumentsModel['checklistRows']
  isLocked: boolean
  onClickDocument: (doc: STDocument) => void
  onClickDeleteDocument: (doc: STDocument) => void
  onClickSetIncompleteReason: (checklistItem: STChecklistItem) => void
  onUpload: (file: File, checklistItemId: string) => void
  className?: string
}

function UploadDocumentsChecklist({
  rows,
  isLocked,
  onClickDocument,
  onClickDeleteDocument,
  onClickSetIncompleteReason,
  onUpload,
  className
}: UploadDocumentsChecklistProps) {
  return (
    <div className={className}>
      {rows.map(({ item, documents, incompleteReason, upload }) => {
        return (
          <DocumentChecklistItem
            key={item.id}
            title={item.name}
            description={item.note}
            completed={item.status == 'complete'}
            faded={item.cancelled}
            className="sm:pl-4"
            buttons={match({ incompleteReason, documents })
              .with({ documents: P.when((documents) => documents.length > 0) }, ({ documents }) => (
                <div className="flex min-w-0 flex-wrap gap-x-4 gap-y-2">
                  {documents.map((document) => (
                    <div
                      key={document.id}
                      className="flex min-w-0 max-w-full items-center gap-2 text-base text-gray-700"
                    >
                      <button
                        type="button"
                        className="flex h-6 min-w-0 items-center gap-2 text-blue-500 underline"
                        onClick={() => onClickDocument(document)}
                      >
                        <PaperClipIcon className="size-3 shrink-0" />
                        <span className="truncate">{document.originalName}</span>
                      </button>
                      {!isLocked && (
                        <button
                          type="button"
                          aria-label={`Delete ${document.originalName}`}
                          onClick={() => onClickDeleteDocument(document)}
                        >
                          <CircleXIcon className="size-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  {item.status === 'incomplete' && !item.cancelled && (
                    <ChecklistUploadActions
                      item={item}
                      upload={upload}
                      isLocked={isLocked}
                      onClickSetIncompleteReason={onClickSetIncompleteReason}
                      onUpload={onUpload}
                    />
                  )}
                </div>
              ))
              .with({ incompleteReason: P.nonNullable }, ({ incompleteReason }) => {
                return (
                  <div
                    className={clsx(
                      'flex min-w-0 items-center gap-4 text-base text-gray-700',
                      isLocked ? 'cursor-default' : 'cursor-pointer'
                    )}
                    onClick={isLocked ? undefined : () => onClickSetIncompleteReason(item)}
                  >
                    <div className="flex h-6 min-w-0 flex-1 items-center gap-2">
                      <MiniCommentIcon className="mt-1 size-3" />
                      <span className="truncate">{incompleteReason.name}</span>
                    </div>
                    {isLocked ? null : (
                      <button type="button">
                        <CircleXIcon className="size-3" />
                      </button>
                    )}
                  </div>
                )
              })
              .otherwise(() => (
                <ChecklistUploadActions
                  item={item}
                  upload={upload}
                  isLocked={isLocked}
                  onClickSetIncompleteReason={onClickSetIncompleteReason}
                  onUpload={onUpload}
                />
              ))}
          />
        )
      })}
    </div>
  )
}

function ChecklistUploadActions({
  item,
  upload,
  isLocked,
  onClickSetIncompleteReason,
  onUpload
}: {
  item: STChecklistItem
  upload: ClientUploadDocumentsModel['checklistRows'][number]['upload']
} & Pick<UploadDocumentsChecklistProps, 'isLocked' | 'onClickSetIncompleteReason' | 'onUpload'>) {
  return (
    <div className="flex gap-3">
      <Button
        variant="default"
        size="l"
        disabled={isLocked}
        aria-label={`Mark ${item.name} not applicable`}
        onClick={() => onClickSetIncompleteReason(item)}
      >
        N/A
      </Button>
      <Button
        type="upload"
        variant="primary"
        size="l"
        disabled={isLocked}
        aria-label={`Upload ${item.name}`}
        leadingIcon={match(upload)
          .with(P.nonNullable, () => <Spinner className="size-4 fill-white text-blue-300" />)
          .otherwise(() => undefined)}
        onUpload={(file) => {
          onUpload(file, item.id)
        }}
      >
        Upload
      </Button>
    </div>
  )
}

type UploadOtherDocumentsProps = {
  title: string
  uploads: Upload[]
  otherDocuments: STDocument[]
  isLocked: boolean
  onClickDocument: (doc: STDocument) => void
  onClickDeleteDocument: (doc: STDocument) => void
  onUpload: (files: File[]) => void
}

function UploadOtherDocuments({
  title,
  uploads,
  otherDocuments,
  isLocked,
  onClickDocument,
  onClickDeleteDocument,
  onUpload
}: UploadOtherDocumentsProps) {
  return (
    <div className="border-gray-200">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">{title}</h2>
      <MiniDocumentDropZone
        caption="Upload any documents that are not listed above"
        disabled={isLocked}
        onDrop={(dt) => {
          const files = [...dt.files]
          if (files.length > 0) {
            onUpload(files)
          }
        }}
      />

      <div>
        {uploads.map((u) => {
          return (
            <OtherDocument
              key={u.id}
              documentName={u.filename}
              progress={u.progress}
              isLocked={false}
            />
          )
        })}

        {/* The bottom margin makes the items more flush with the card, but if there are no items we don't want kill the padding */}
        <div className={clsx('-mx-6 -mb-6', otherDocuments.length == 0 && 'hidden')}>
          {otherDocuments.map((doc) => (
            <OtherDocument
              key={doc.id}
              documentName={doc.originalName!}
              isLocked={isLocked}
              onClickDocument={() => onClickDocument(doc)}
              onClickDelete={() => onClickDeleteDocument(doc)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function OtherDocument({
  documentName,
  progress,
  onClickDocument,
  onClickDelete,
  isLocked = false
}: {
  documentName: string
  progress?: Progress
  onClickDocument?: () => void
  onClickDelete?: () => void
  isLocked: boolean
}) {
  return (
    <div
      className={clsx(
        'flex flex-col items-end justify-end border-b border-gray-200 px-6 py-3 last:border-b-0'
      )}
    >
      <div className="flex w-full min-w-0 items-center gap-2 text-base text-gray-700">
        <div
          className={clsx(
            'flex h-6 min-w-0 flex-1 items-center gap-2',
            onClickDocument && 'cursor-pointer text-blue-500 underline'
          )}
          onClick={onClickDocument}
        >
          <PaperClipIcon className="size-3" />
          <div className="min-w-0 flex-1 truncate">{documentName}</div>
        </div>
        {isLocked ? null : (
          <button type="button" onClick={onClickDelete}>
            <CircleXIcon className="size-3" />
          </button>
        )}
      </div>
      {progress && (
        <div className="h-1 w-full bg-gray-300">
          <div className="h-full bg-blue-500" style={{ width: Progress.toString(progress) }} />
        </div>
      )}
    </div>
  )
}

function SetChecklistItemIncompleteReasonDialog({
  reason,
  missingReasonOptions,
  cancelLabel,
  onChange,
  onSave,
  onCancel
}: {
  reason: string | undefined
  missingReasonOptions: STMissingReason[]
  cancelLabel: string
  onChange: (reason: string | undefined) => void
  onSave: () => void
  onCancel: () => void
}) {
  return (
    <Dialog
      title="Not applicable"
      subtitle="If this is no longer applicable or you can't provide it yet, please select a reason below."
      buttons={
        <DialogButtons>
          <Button variant="default" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button onClick={onSave}>Save reason</Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-row p-1">
        <Select
          placeholder="Select a reason"
          className="w-full"
          options={missingReasonOptions}
          value={reason}
          buildValue={(option) => option.key}
          buildLabel={(option) => option.name}
          onChange={onChange}
        />
      </div>
    </Dialog>
  )
}

function MiniCommentIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 12 12">
      <path
        fill="currentColor"
        d="M1 11V1.98A.95.95 0 0 1 1.95 1h8.1q.4 0 .67.28.28.29.28.7v6.14a.95.95 0 0 1-.95.97H2.86zm1.53-2.72h7.52q.06 0 .11-.05a.2.2 0 0 0 .05-.11V1.98a.2.2 0 0 0-.05-.12.2.2 0 0 0-.11-.05h-8.1a.2.2 0 0 0-.11.05.2.2 0 0 0-.05.12v7.05z"
      />
      <path
        fill="currentColor"
        d="M8.87 3.93c0 .23-.19.42-.43.42h-5A.43.43 0 0 1 3 3.93c0-.24.2-.43.43-.43h5.02c.24 0 .42.19.42.42M8.87 5.93c0 .23-.19.42-.43.42h-5A.43.43 0 0 1 3 5.93c0-.24.2-.43.43-.43h5.02c.24 0 .42.19.42.42"
      />
    </svg>
  )
}
